import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { generateId } from 'utils/string';
import { omitProps, deriveStateFromWatchedProps } from 'utils/react';
import { VisuallyHidden } from 'components/Utilities/VisuallyHidden';
import SelectableContext from './SelectableContext';
import { INPUT_TYPE, VERTICAL_ALIGN } from './SelectableItem';

export default class Selectable extends Component {
    static displayName = 'Selectable';

    static TYPE = INPUT_TYPE;

    static VERTICAL_ALIGN = VERTICAL_ALIGN;

    static propTypes = {
        id: PropTypes.string,
        type: PropTypes.oneOf(INPUT_TYPE.values()),
        label: PropTypes.node,
        verticalAlign: PropTypes.oneOf(VERTICAL_ALIGN.values()),
        selectedItems: PropTypes.arrayOf(PropTypes.string),
        selectOnClick: PropTypes.bool,
        disableUncheckedItems: PropTypes.bool,
        propagateClicksOnInput: PropTypes.bool,
        onSelect: PropTypes.func,
        component: PropTypes.elementType,
        className: PropTypes.string,
        children: PropTypes.node,
    };

    static defaultProps = {
        id: '',
        type: INPUT_TYPE.CHECKBOX,
        label: null,
        verticalAlign: VERTICAL_ALIGN.MIDDLE,
        selectedItems: [],
        selectOnClick: true,
        disableUncheckedItems: false,
        propagateClicksOnInput: false,
        onSelect: noop,
        component: null,
        className: null,
        children: null,
    };

    static getDerivedStateFromProps(props, state) {
        return deriveStateFromWatchedProps(props, state, {
            id: () => props.id || generateId('selectable'),
            selectedItems: null,
        });
    }

    state = {
        id: null,
        selectedItems: [],
    };

    getContext() {
        const {
            disableUncheckedItems,
            propagateClicksOnInput,
            selectOnClick,
            type,
            verticalAlign,
        } = this.props;
        const { id } = this.state;

        return {
            id,
            inputType: type,
            verticalAlign,
            selectOnClick,
            disableUncheckedItems,
            propagateClicksOnInput,
            isItemSelected: this.isItemSelected,
            toggleItem: this.toggleItem,
        };
    }

    toggleItem = (itemValue) => {
        const { type } = this.props;

        if (type !== Selectable.TYPE.RADIO && this.isItemSelected(itemValue)) {
            this.deselectItem(itemValue);
        } else {
            this.selectItem(itemValue);
        }
    };

    isItemSelected = (itemValue) => {
        const { selectedItems } = this.state;

        if (!selectedItems || selectedItems.length === 0) return false;

        return selectedItems.includes(itemValue);
    };

    selectItem = (itemValue) => {
        const { type, onSelect } = this.props;
        const { selectedItems } = this.state;

        if (this.isItemSelected(itemValue)) return;

        if (type === Selectable.TYPE.RADIO) {
            this.setState({ selectedItems: [itemValue] });

            if (onSelect) onSelect([itemValue]);
        } else {
            const newSelection = [...selectedItems, itemValue];
            this.setState({ selectedItems: newSelection });

            if (onSelect) onSelect(newSelection);
        }
    };

    deselectItem = (itemValue) => {
        const { onSelect } = this.props;
        const { selectedItems } = this.state;

        const newSelection = selectedItems.filter((selectedValue) => selectedValue !== itemValue);
        this.setState({ selectedItems: newSelection });

        if (onSelect) onSelect(newSelection);
    };

    renderChildren() {
        const { label, component, className, children } = this.props;
        const { id } = this.state;

        const labelId = label ? `${id}-label` : undefined;
        const rest = omitProps(this.props, Selectable);

        if (!component && !className && !label && Object.keys(rest).length === 0) {
            return children;
        }

        const ContainerComponent = component || 'div';
        return (
            <ContainerComponent
                role="group"
                id={id}
                aria-labelledby={labelId}
                className={className}
                {...rest}
            >
                {label && <VisuallyHidden id={labelId}>{label}</VisuallyHidden>}
                {children}
            </ContainerComponent>
        );
    }

    render() {
        return (
            <SelectableContext.Provider value={this.getContext()}>
                {this.renderChildren()}
            </SelectableContext.Provider>
        );
    }
}
